import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export const adminKey = () => localStorage.getItem("fdm_admin_key") || "";
export const adminHeaders = () => ({ headers: { "X-Admin-Key": adminKey() } });

export const money = (n) => {
  const v = Number(n || 0);
  const cents = Math.abs(v - Math.round(v)) > 0.004;
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
};

export const effectiveOptions = (product, optionSets = {}) => {
  const set = product.option_set_id ? optionSets[product.option_set_id] : null;
  return {
    variants: [...(product.variants || []), ...(set?.variants || [])].filter((g) => g.options?.length),
    addons: [...(product.addons || []), ...(set?.addons || [])],
  };
};

export const defaultSelection = (product, optionSets) => {
  const { variants, addons } = effectiveOptions(product, optionSets);
  return {
    variants: Object.fromEntries(variants.map((g) => [g.name, g.options[0].label])),
    addons: addons.filter((a) => a.default).map((a) => a.label),
  };
};

export const MULTI_SEP = " + ";
const QTY_RE = /^(.*?)\s*[×x](\d+)$/;

// "a + b ×3" -> [{label:"a",qty:1},{label:"b",qty:3}]
export const parseMulti = (v) => {
  const out = [];
  for (const raw of String(v || "").split(MULTI_SEP)) {
    const part = raw.trim();
    if (!part || part === "None") continue;
    const m = part.match(QTY_RE);
    const label = m ? m[1].trim() : part;
    const qty = m ? Number(m[2]) : 1;
    const i = out.findIndex((x) => x.label === label);
    if (i >= 0) out[i].qty = qty;
    else out.push({ label, qty });
  }
  return out;
};
const joinMulti = (items) => (items.length ? items.map((x) => (x.qty > 1 ? `${x.label} ×${x.qty}` : x.label)).join(MULTI_SEP) : "None");
export const splitMulti = (v) => parseMulti(v).map((x) => x.label);
export const multiQty = (v, label) => parseMulti(v).find((x) => x.label === label)?.qty || 0;

export const unitPrice = (product, chosen = {}, chosenAddons = [], optionSets = {}) => {
  const { variants, addons } = effectiveOptions(product, optionSets);
  const base = variants.reduce((sum, g) => {
    if (g.multi) {
      const picked = Object.fromEntries(parseMulti(chosen[g.name]).map((x) => [x.label, x.qty]));
      return sum + g.options.reduce((s, o) => s + (picked[o.label] ? Number(o.price_delta || 0) * (o.per_unit ? picked[o.label] : 1) : 0), 0);
    }
    const opt = g.options.find((o) => o.label === chosen[g.name]) || g.options[0];
    return sum + Number(opt?.price_delta || 0);
  }, Number(product.price || 0));
  const picked = new Set(chosenAddons);
  return addons.reduce((sum, a) => sum + (picked.has(a.label) ? Number(a.price || 0) : 0), base);
};

export const toggleMulti = (current, label) => {
  if (label === "None") return "None";
  const items = parseMulti(current);
  const i = items.findIndex((x) => x.label === label);
  if (i >= 0) items.splice(i, 1);
  else items.push({ label, qty: 1 });
  return joinMulti(items);
};

export const setMultiQty = (current, label, qty) => {
  const items = parseMulti(current).filter((x) => x.label !== label);
  if (qty > 0) items.push({ label, qty });
  return joinMulti(items);
};

export const hasPricedOptions = (product, optionSets = {}) => {
  const { variants, addons } = effectiveOptions(product, optionSets);
  return variants.some((g) => g.options.some((o) => Number(o.price_delta) > 0)) || addons.length > 0;
};

export const variantText = (v = {}, addons = []) =>
  [...Object.entries(v).filter(([, val]) => val !== "None").map(([k, val]) => `${k}: ${val}`), ...addons].join(
    " · "
  );

export const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const quotePdfUrl = (id) => `${API}/quotes/${id}/pdf`;
